import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      if (isAdmin) {
        const [statsRes, expiringRes] = await Promise.all([
          subscriptionsAPI.getStats(),
          subscriptionsAPI.getExpiring()
        ]);
        setStats(statsRes.data);
        setExpiring(expiringRes.data);
      } else {
        const res = await subscriptionsAPI.getAll();
        setExpiring(res.data.filter(s => s.status === 'expiring_soon'));
      }
    } catch (error) {
      toast.error('Gagal memuat data dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Dashboard</h1>
        {isAdmin && (
          <Link to="/subscriptions/new" className="btn-primary text-sm sm:text-base">
            + Tambah
          </Link>
        )}
      </div>

      {isAdmin && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          <StatCard title="Total" value={stats.total} icon="📊" color="bg-blue-500" />
          <StatCard title="Aktif" value={stats.active} icon="✅" color="bg-green-500" />
          <StatCard title="Akan Berakhir" value={stats.expiringSoon} icon="⚠️" color="bg-yellow-500" />
          <StatCard title="Berakhir" value={stats.expired} icon="❌" color="bg-red-500" />
        </div>
      )}

      {isAdmin && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="card">
            <h3 className="text-base sm:text-lg font-semibold mb-4">Statistik Pembelian</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Bulan Ini</span>
                <span className="font-semibold">{stats.thisMonthPurchases}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tahun Ini</span>
                <span className="font-semibold">{stats.purchasesThisYear}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Pengeluaran</span>
                <span className="font-semibold text-green-600">
                  Rp {stats.totalExpenditure?.toLocaleString('id-ID') || 0}
                </span>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-base sm:text-lg font-semibold mb-4">Aksi Cepat</h3>
            <div className="space-y-3">
              <Link
                to="/subscriptions?status=expiring_soon"
                className="block p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
              >
                <span className="font-medium text-yellow-800 text-sm">
                  Akan Berakhir ({stats.expiringIn30Days})
                </span>
              </Link>
              <Link
                to="/reports"
                className="block p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <span className="font-medium text-blue-800 text-sm">Lihat Laporan</span>
              </Link>
              {isAdmin && (
                <Link
                  to="/users"
                  className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span className="font-medium text-gray-800 text-sm">Kelola User</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold">Perlu Perhatian</h3>
          <Link to="/subscriptions" className="text-primary-600 hover:text-primary-700 text-xs sm:text-sm">
            Lihat Semua
          </Link>
        </div>

        {expiring.length === 0 ? (
          <p className="text-gray-500 text-center py-8 text-sm">
            Tidak ada langganan yang perlu diperhatikan
          </p>
        ) : (
          <div className="space-y-3 sm:space-y-0 sm:overflow-x-auto">
            {expiring.slice(0, 5).map((sub) => (
              <div key={sub.id} className="sm:hidden border-b border-gray-100 pb-3 last:border-0">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-sm">{sub.user?.name}</p>
                    <p className="text-xs text-gray-500">{sub.zoomAccountEmail}</p>
                  </div>
                  <StatusBadge status={sub.status} />
                </div>
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span className="text-gray-500">
                    {new Date(sub.expiryDate).toLocaleDateString('id-ID')}
                  </span>
                  <DaysRemaining date={sub.expiryDate} />
                </div>
              </div>
            ))}

            <table className="w-full hidden sm:table">
              <thead>
                <tr className="table-header">
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Email Zoom</th>
                  <th className="px-4 py-3">Berakhir</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Sisa</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {expiring.slice(0, 5).map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium">{sub.user?.name}</p>
                      <p className="text-sm text-gray-500">{sub.user?.department}</p>
                    </td>
                    <td className="px-4 py-3 text-sm">{sub.zoomAccountEmail}</td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(sub.expiryDate).toLocaleDateString('id-ID')}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={sub.status} /></td>
                    <td className="px-4 py-3"><DaysRemaining date={sub.expiryDate} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color }) => (
  <div className="card p-3 sm:p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs sm:text-sm text-gray-500">{title}</p>
        <p className="text-xl sm:text-3xl font-bold mt-1">{value}</p>
      </div>
      <div className={`${color} p-2 sm:p-3 rounded-full text-white text-lg sm:text-2xl`}>
        {icon}
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-green-100 text-green-800',
    expiring_soon: 'bg-yellow-100 text-yellow-800',
    expired: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800'
  };
  const labels = {
    active: 'Aktif',
    expiring_soon: 'Akan Berakhir',
    expired: 'Berakhir',
    cancelled: 'Dibatalkan'
  };
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

const DaysRemaining = ({ date }) => {
  const days = Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0) return <span className="text-red-600 font-medium">Berakhir</span>;
  if (days <= 7) return <span className="text-red-600 font-medium">{days}h</span>;
  if (days <= 30) return <span className="text-yellow-600 font-medium">{days}h</span>;
  return <span className="text-green-600 font-medium">{days}h</span>;
};

export default Dashboard;
